import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Loader2 } from 'lucide-react';
import { getAutoImportRuns } from '../../services/api';

const when = (iso) => new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const TONE = { completed: 'text-emerald-600 dark:text-emerald-400', running: 'text-indigo-600 dark:text-indigo-400', failed: 'text-red-600 dark:text-red-400' };

const JobLink = ({ j, children }) => {
  const navigate = useNavigate();
  return (
    <li>
      <button onClick={() => navigate(`/jobs/${j.id}`)} title={typeof children === 'string' ? children : j.title}
        className="w-full text-left break-words hover:text-primary transition-colors" data-testid={`auto-import-job-link-${j.id}`}>
        <span className="font-mono text-muted-foreground">{j.code}</span> · {children}
      </button>
    </li>
  );
};

const UpdatedJob = ({ j }) => (
  <JobLink j={j}>
    {j.old_title ? <><span className="line-through text-muted-foreground">{j.old_title}</span> → {j.title}</> : j.title}
    {j.status && <span className="text-muted-foreground"> · now {j.status}</span>}
  </JobLink>
);

const RunRow = ({ run, open, onToggle }) => {
  const jobs = run.jobs || [];
  const updated = run.updated_jobs || [];
  const label = run.status === 'completed' ? `+${run.imported.toLocaleString()}` : run.status === 'running' ? 'Running' : 'Failed';
  const who = run.trigger === 'cron' ? 'scheduled' : `manual${run.by ? ` · ${run.by}` : ''}`;
  const expandable = jobs.length > 0 || updated.length > 0;
  return (
    <div className="text-[11px]" data-testid={`auto-import-run-row-${run.id}`}>
      <button onClick={onToggle} disabled={!expandable} className="w-full flex items-center gap-2 py-1 px-1 rounded text-left hover:bg-muted/40 transition-colors disabled:cursor-default">
        <span className={`font-semibold w-12 shrink-0 ${TONE[run.status] || ''}`}>{label}</span>
        <span className="text-muted-foreground flex-1 min-w-0 break-words">
          {run.mode === 'catch_up' ? 'Catch-up' : 'New jobs'} · {who} · {run.found.toLocaleString()} checked
          {run.updated > 0 && <span data-testid={`auto-import-run-updated-${run.id}`}> · {run.updated.toLocaleString()} updated</span>}
        </span>
        <span className="text-muted-foreground shrink-0">{when(run.started_at)}</span>
        {expandable && <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {run.status === 'failed' && <p className="px-1 text-red-600/80 dark:text-red-400/80 truncate">{run.error}</p>}
      {open && (
        <ul className="pl-14 pb-1 space-y-0.5">
          {jobs.map((j) => <JobLink key={j.id} j={j}>{j.title}</JobLink>)}
          {run.imported > jobs.length && <li className="text-muted-foreground">+{(run.imported - jobs.length).toLocaleString()} more — see the Jobs page</li>}
          {updated.length > 0 && <li className="pt-1 font-semibold text-muted-foreground">Updated from Ceipal edits</li>}
          {updated.map((j) => <UpdatedJob key={`u-${j.id}`} j={j} />)}
          {run.updated > updated.length && <li className="text-muted-foreground">+{(run.updated - updated.length).toLocaleString()} more updated</li>}
        </ul>
      )}
    </div>
  );
};

export const AutoImportLog = ({ regionKey, refreshKey }) => {
  const [runs, setRuns] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { getAutoImportRuns(regionKey).then((r) => setRuns(r.runs)).catch(() => setRuns([])); }, [regionKey, refreshKey]);

  if (!runs) return <div className="flex justify-center py-3"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>;
  if (!runs.length) return <p className="text-[11px] text-muted-foreground border-t pt-2" data-testid={`auto-import-log-empty-${regionKey}`}>No checks yet.</p>;
  return (
    <div className="border-t pt-2 space-y-0.5 max-h-72 overflow-y-auto" data-testid={`auto-import-log-${regionKey}`}>
      {runs.map((r) => <RunRow key={r.id} run={r} open={open === r.id} onToggle={() => setOpen(open === r.id ? null : r.id)} />)}
    </div>
  );
};

export default AutoImportLog;
